import React from 'react';
import root from 'window-or-global';
import {RouteComponentProps} from 'react-router';
import {HashLink as ReactRouterLink, NavHashLink as NavLink} from 'react-router-hash-link';
import {generateRoutePath} from '@epic-core/common';
import withRouterRef from '../../hocs/withRouterRef';

export interface LinkProps {
    activeClassName?: string;
    ariaControls?: string;
    ariaExpanded?: boolean;
    ariaHidden?: boolean;
    ariaLabel?: string;
    ariaLabelledby?: string;
    ariaSelected?: boolean;
    children: React.ReactNode;
    className?: string;
    forwardRef?: React.Ref<HTMLAnchorElement> | null;
    id?: string;
    isActive?: () => boolean;
    isExternalUrl?: boolean;
    onClick?: (e?) => void;
    resetScroll?: boolean;
    role?: string;
    tabIndex?: number;
    target?: '_self' | '_blank';
    to: string;
    exact?: boolean;
    smooth?: boolean;
    scroll?: (element: HTMLElement) => void;
    dontIncludeLocale?: boolean;
    download?: boolean;
}

const Link: React.FC<LinkProps & RouteComponentProps> = ({
    activeClassName,
    ariaControls,
    ariaExpanded,
    ariaHidden,
    ariaLabel,
    ariaLabelledby,
    ariaSelected,
    children,
    className,
    forwardRef,
    id,
    isActive,
    isExternalUrl = false,
    onClick,
    resetScroll = false,
    role = 'link',
    tabIndex,
    target = '_blank',
    to,
    exact,
    smooth,
    scroll,
    dontIncludeLocale = false,
    download = false
}) => {
    if (/^[\w.]+:\/\//.test(to)) {
        const isAppURLReg = new RegExp(
            `^(?:https?://(?:www\\.)?)?epicgames\\.com${root.appContext}`,
            'i'
        );

        const linkProps = !isAppURLReg.test(to) && {
            rel: 'noopener noreferrer',
            target
        };

        return (
            <a
                id={id}
                href={to}
                className={className}
                aria-controls={ariaControls}
                aria-expanded={ariaExpanded}
                aria-hidden={ariaHidden}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                aria-selected={ariaSelected}
                tabIndex={tabIndex}
                onClick={onClick}
                ref={forwardRef}
                role={role}
                download={download}
                {...linkProps}>
                {children}
            </a>
        );
    }

    if (isExternalUrl) {
        return (
            <a
                id={id}
                href={generateRoutePath(to, true)}
                aria-controls={ariaControls}
                aria-expanded={ariaExpanded}
                aria-hidden={ariaHidden}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledby}
                aria-selected={ariaSelected}
                ref={forwardRef}
                tabIndex={tabIndex}
                className={className}
                onClick={onClick}
                role={role}
                download={download}>
                {children}
            </a>
        );
    }

    const LinkComponent = activeClassName ? NavLink : ReactRouterLink;
    const linkProps: Partial<LinkProps> = activeClassName ? {isActive, activeClassName, exact} : {};

    const routePath = generateRoutePath(to, dontIncludeLocale);
    let pathname = routePath;
    let search = '';
    const hasQuery = /\?/.test(to);

    if (hasQuery) {
        pathname = routePath.substring(0, routePath.indexOf('?'));
        search = routePath.substring(routePath.indexOf('?'));
    }

    const toWithState: Record<'pathname' | 'search' | 'state', any> = {
        pathname,
        search,
        state: {resetScroll}
    };

    return (
        <LinkComponent
            className={className}
            id={id}
            to={resetScroll ? toWithState : generateRoutePath(to, dontIncludeLocale)}
            tabIndex={tabIndex}
            aria-controls={ariaControls}
            aria-expanded={ariaExpanded}
            aria-hidden={ariaHidden}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            aria-selected={ariaSelected}
            innerRef={forwardRef}
            onClick={onClick}
            role={role}
            smooth={smooth}
            scroll={scroll}
            download={download}
            {...linkProps}>
            {children}
        </LinkComponent>
    );
};

export default withRouterRef<LinkProps>(Link);
